import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { CCard, CCardBody } from "@coreui/react";
import DateRangeDropdown from "./DateRangeDropdown";

const PALETTE = [
  "#1E40AF",
  "#DC2626",
  "#16A34A",
  "#F97316",
  "#EAB308",
  "#9966FF",
  "#36A2EB",
  "#FF6384",
  "#4BC0C0",
  "#FFCE56",
];

// (d) Total successful transaction amount by day / month / year, stacked by
// insurance product or by payment type.
const TransactionAmountCard = ({
  amountData,
  dateRange,
  setDateRange,
  groupBy,
  setGroupBy,
  split,
  setSplit,
}) => {
  const chartRef = useRef(null);

  const series = (amountData && amountData.series) || [];
  const totalAmount = (amountData && amountData.totalAmount) || 0;

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }
    const chart = echarts.init(chartRef.current);

    // Union of split names across the whole range, so every bucket stacks the
    // same series in the same order.
    const names = [];
    series.forEach((row) => {
      row.breakdown.forEach((item) => {
        if (names.indexOf(item.name) === -1) {
          names.push(item.name);
        }
      });
    });

    const amountFor = (row, name) => {
      const match = row.breakdown.filter((item) => item.name === name)[0];
      return match ? match.amount : 0;
    };

    const option = {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        valueFormatter: (value) =>
          `RM ${(value || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
      },
      grid: {
        bottom: "18%",
      },
      legend: {
        bottom: "0%",
        left: "center",
        type: "scroll",
      },
      xAxis: {
        type: "category",
        data: series.map((row) => row.period),
        axisLabel: {
          rotate: series.length > 12 ? 45 : 0,
        },
      },
      yAxis: {
        type: "value",
      },
      series: names.map((name, index) => ({
        name,
        type: "bar",
        stack: "amount",
        data: series.map((row) => amountFor(row, name)),
        itemStyle: { color: PALETTE[index % PALETTE.length] },
      })),
    };

    chart.setOption(option);

    return () => {
      chart.dispose();
    };
  }, [amountData]);

  return (
    <CCard className="border-light">
      <CCardBody style={{ overflowX: "auto" }}>
        <span>Transaction Amount (RM)</span>
        <ul className="nav nav-pills mt-3">
          {[
            ["day", "Daily"],
            ["month", "Monthly"],
            ["year", "Yearly"],
          ].map(([value, label]) => (
            <li className="nav-item" key={value}>
              <a
                className={`nav-link ${groupBy === value ? "active" : ""}`}
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setGroupBy(value);
                }}
              >
                {label}
              </a>
            </li>
          ))}
          <li className="nav-item" style={{ marginLeft: "20px" }}>
            <a
              className={`nav-link ${split === "product" ? "active" : ""}`}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSplit("product");
              }}
            >
              By Product
            </a>
          </li>
          <li className="nav-item">
            <a
              className={`nav-link ${split === "method" ? "active" : ""}`}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSplit("method");
              }}
            >
              By Payment Type
            </a>
          </li>
        </ul>
        <DateRangeDropdown dateRange={dateRange} setDateRange={setDateRange} />
        <div
          ref={chartRef}
          style={{ height: "400px", width: "100%", margin: "5px auto" }}
        ></div>
        <div style={{ textAlign: "center", marginTop: "10px" }}>
          <span style={{ fontSize: "13px", color: "#555E67" }}>
            Total:{" "}
            <strong>
              RM{" "}
              {totalAmount.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </strong>
          </span>
        </div>
      </CCardBody>
    </CCard>
  );
};

export default TransactionAmountCard;
